/**
 * Générateur HTML de Listes de Présence pour examens.
 *
 * Produit un document HTML imprimable (A4) avec :
 * - En-tête institutionnel (logo, titre, session)
 * - Infos épreuve : matière(s) avec classes associées, date, horaire, site, salle
 * - Classes concernées (badges)
 * - Surveillants (lignes vides)
 * - Tableau : N°, Nom, Prénom, Classe, Émargement avant/après dépôt
 * - Observations / Procès-verbal
 * - Bloc signature en dernière page
 *
 * Une seule page par salle : toutes les matières du créneau y figurent.
 */

import type { PlacementEtudiant } from '../../services/brassageApi';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PresenceMatiereInfo {
  nom: string;
  classes: string[];  // classes concernées par cette matière dans la salle
}

export interface PresenceCreneauInfo {
  date: string;          // "2025-08-18"
  jour_semaine: string;  // "LUNDI"
  heure_debut: string;   // "08:00" ou "08H00" ou "08:00:00"
  heure_fin: string;     // "10:00" ou "10H00" ou "10:00:00"
  matieres: PresenceMatiereInfo[];  // matières avec classes associées
  enseignant_noms?: string[];
}

export interface PresenceSalleData {
  site_nom: string;
  salle_nom: string;
  effectif: number;
  placements: PlacementEtudiant[];
  creneau?: PresenceCreneauInfo;  // optionnel : si emploi du temps fourni
}

export interface PresencePrintOptions {
  fontFamily?: string;
  fontScale?: number;   // pourcentage (100 = normal)
  sessionLabel?: string;  // ex: "Examen Partiel: Session Ordinaire d'Août 2025"
  anneeAcademique?: string;
  nombreLignesSurveillants?: number;  // défaut: 4
  nombreLignesObservations?: number;  // défaut: 5
  /** Identité institut (ville + chef scolarité) — depuis useGetInfoInstitutQuery */
  institutVille?: string;
  chefScolariteNom?: string;
  chefScolariteTitre?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtDateFr = (d: string): string => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

/** Formate "08:00:00" ou "08:00" → "08H00" */
const fmtHeure = (h: string): string => {
  if (!h) return '';
  // Supprimer les secondes si présentes (08:00:00 → 08:00)
  const sansSecondes = h.replace(/^(\d{2})[H:](\d{2}).*$/, '$1:$2');
  return sansSecondes.replace(':', 'H');
};

/** Extrait les classes uniques triées à partir des placements. */
function extractClasses(placements: PlacementEtudiant[]): string[] {
  const set = new Map<string, boolean>();
  placements.forEach(p => {
    if (p.etudiant_classe) set.set(p.etudiant_classe, true);
  });
  return Array.from(set.keys()).sort();
}

// ─── Génération d'une page de liste de présence ─────────────────────────────

function buildPresencePageHTML(
  salle: PresenceSalleData,
  opts: PresencePrintOptions,
): string {
  const scale = (opts.fontScale || 100) / 100;
  const nbSurveillants = opts.nombreLignesSurveillants ?? 4;
  const nbObservations = opts.nombreLignesObservations ?? 5;
  const classes = extractClasses(salle.placements);
  const sessionLabel = opts.sessionLabel || 'Examen Partiel';
  const creneau = salle.creneau;

  // Tri des étudiants par nom puis prénom
  const sorted = [...salle.placements].sort((a, b) => {
    const cmp = a.etudiant_nom.localeCompare(b.etudiant_nom);
    return cmp !== 0 ? cmp : a.etudiant_prenoms.localeCompare(b.etudiant_prenoms);
  });

  // Lignes du tableau
  const tableRows = sorted.map((p, i) => {
    const bg = i % 2 === 0 ? '#fff' : '#f5f5f5';
    return `<tr style="background:${bg};">
      <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:${9 * scale}px;">${i + 1}</td>
      <td style="border:1px solid #000;padding:2px 6px;font-size:${10 * scale}px;font-weight:bold;">${p.etudiant_nom}</td>
      <td style="border:1px solid #000;padding:2px 6px;font-size:${10 * scale}px;">${p.etudiant_prenoms}</td>
      <td style="border:1px solid #000;padding:2px 4px;text-align:center;font-size:${9 * scale}px;">${p.etudiant_classe}</td>
      <td style="border:1px solid #000;padding:2px 4px;min-width:80px;">&nbsp;</td>
      <td style="border:1px solid #000;padding:2px 4px;min-width:80px;">&nbsp;</td>
    </tr>`;
  }).join('');

  // Badges des classes
  const classesBadges = classes.map(c =>
    `<span style="display:inline-block;padding:1px 6px;margin:1px 2px;background:#dbeafe;color:#1e40af;border-radius:3px;font-size:${8 * scale}px;font-weight:bold;">${c}</span>`
  ).join('');

  // Lignes surveillants
  const surveillantLines = Array.from({ length: nbSurveillants }, (_, i) =>
    `<tr>
      <td style="border-bottom:1px dotted #999;padding:4px 2px;font-size:${9 * scale}px;width:20px;">${i + 1}.</td>
      <td style="border-bottom:1px dotted #999;padding:4px 2px;width:45%;">&nbsp;</td>
      <td style="border-bottom:1px dotted #999;padding:4px 2px;font-size:${8 * scale}px;text-align:center;">Signature : </td>
    </tr>`
  ).join('');

  // Lignes observations
  const observationLines = Array.from({ length: nbObservations }, () =>
    `<div style="border-bottom:1px dotted #999;height:${18 * scale}px;margin-bottom:2px;">&nbsp;</div>`
  ).join('');

  // Matière(s) / date / horaire
  let moduleField: string;
  if (creneau && creneau.matieres.length > 0) {
    // Afficher chaque matière avec ses classes associées
    moduleField = creneau.matieres.map(m => {
      const classesStr = m.classes.length > 0
        ? ` <span style="font-size:${7 * scale}px;color:#666;font-weight:normal;">(${m.classes.join(', ')})</span>`
        : '';
      return `<span style="display:inline-block;padding:1px 6px;margin:1px 2px;background:#fef3c7;color:#92400e;border-radius:3px;font-size:${9 * scale}px;font-weight:bold;">${m.nom}${classesStr}</span>`;
    }).join(' ');
  } else {
    moduleField = '____________________________________________';
  }

  const dateField = creneau
    ? `<strong>${creneau.jour_semaine} ${fmtDateFr(creneau.date)}</strong>`
    : '______/______/________';
  const heureDebutField = creneau
    ? `<strong>${fmtHeure(creneau.heure_debut)}</strong>`
    : '______H______';
  const heureFinField = creneau
    ? `<strong>${fmtHeure(creneau.heure_fin)}</strong>`
    : '______H______';

  return `
<!-- En-tête institutionnel -->
<table style="width:100%;margin-bottom:4px;"><tr>
  <td style="width:75px;vertical-align:top;text-align:center;">
    <img src="/logo.png" alt="Logo" style="width:70px;height:70px;object-fit:contain;" onerror="this.style.display='none'" />
  </td>
  <td style="vertical-align:top;text-align:center;line-height:1.35;">
    <div style="font-weight:bold;font-size:${12 * scale}px;text-transform:uppercase;">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE</div>
    <div style="font-size:${8 * scale}px;">Agrément définitif par l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
    <div style="border-top:1px solid #000;margin:3px auto;width:60%;"></div>
    <div style="font-weight:bold;font-size:${11 * scale}px;text-transform:uppercase;">${sessionLabel}</div>
  </td>
</tr></table>

<!-- Titre -->
<div style="text-align:center;font-weight:bold;font-size:${14 * scale}px;margin:6px 0 4px 0;text-transform:uppercase;text-decoration:underline;">
  LISTE DE PRÉSENCE
</div>

<!-- Infos épreuve -->
<table style="width:100%;margin-bottom:4px;font-size:${10 * scale}px;">
  <tr>
    <td colspan="2" style="padding:2px 0;">Module : ${moduleField}</td>
  </tr>
  <tr>
    <td style="padding:2px 0;">Date : ${dateField}</td>
    <td style="padding:2px 0;text-align:right;">Site : <strong>${salle.site_nom}</strong> &nbsp;&nbsp; Salle : <strong>${salle.salle_nom}</strong></td>
  </tr>
  <tr>
    <td style="padding:2px 0;">Heure début : ${heureDebutField} &nbsp;&nbsp;&nbsp; Heure fin : ${heureFinField}</td>
    <td style="padding:2px 0;text-align:right;">Effectif : <strong>${salle.effectif}</strong></td>
  </tr>
</table>

<!-- Classes concernées -->
<div style="margin-bottom:4px;font-size:${9 * scale}px;">
  Classes : ${classesBadges}
</div>

<!-- Surveillants -->
<div style="margin-bottom:6px;">
  <div style="font-weight:bold;font-size:${9 * scale}px;margin-bottom:2px;text-decoration:underline;">Surveillant(s) :</div>
  <table style="width:70%;font-size:${9 * scale}px;">
    ${surveillantLines}
  </table>
</div>

<!-- Tableau de présence -->
<table style="width:100%;border-collapse:collapse;">
  <thead>
    <tr>
      <th style="border:1px solid #000;padding:3px 4px;font-size:${9 * scale}px;background:#FFD700;text-align:center;width:28px;">N°</th>
      <th style="border:1px solid #000;padding:3px 6px;font-size:${9 * scale}px;background:#FFD700;text-align:left;width:25%;">Nom</th>
      <th style="border:1px solid #000;padding:3px 6px;font-size:${9 * scale}px;background:#FFD700;text-align:left;width:25%;">Prénom</th>
      <th style="border:1px solid #000;padding:3px 4px;font-size:${9 * scale}px;background:#FFD700;text-align:center;width:55px;">Classe</th>
      <th style="border:1px solid #000;padding:3px 4px;font-size:${9 * scale}px;background:#FFD700;text-align:center;">Avant dépôt</th>
      <th style="border:1px solid #000;padding:3px 4px;font-size:${9 * scale}px;background:#FFD700;text-align:center;">Après dépôt</th>
    </tr>
  </thead>
  <tbody>
    ${tableRows}
  </tbody>
</table>

<!-- Observations / Procès-verbal -->
<div style="margin-top:10px;">
  <div style="font-weight:bold;font-size:${9 * scale}px;margin-bottom:3px;text-decoration:underline;">Observations / Procès-verbal :</div>
  <div style="font-size:${8 * scale}px;color:#555;margin-bottom:3px;font-style:italic;">(Mentionner tout incident survenu pendant l'épreuve : retard, fraude, absence de copie, etc.)</div>
  ${observationLines}
</div>
`;
}

// ─── Génération du document complet multi-pages ─────────────────────────────

export function buildListePresenceHTML(
  salles: PresenceSalleData[],
  opts: PresencePrintOptions,
): string {
  const ff = opts.fontFamily || "'Arial', Helvetica, sans-serif";
  const scale = (opts.fontScale || 100) / 100;

  // Générer les pages : une page par salle
  const pageGroups: { html: string; label: string }[] = [];

  for (const salle of salles) {
    pageGroups.push({
      html: buildPresencePageHTML(salle, opts),
      label: `${salle.site_nom} — ${salle.salle_nom}`,
    });
  }

  // Ajouter bloc signature sur la dernière page
  // Ville + chef configurables depuis useGetInfoInstitutQuery (PN vs BZ)
  const signatureBlock = `
<div style="margin-top:30px;text-align:right;font-size:${10 * scale}px;">
  <div>Fait à ${opts?.institutVille || 'Pointe-Noire'}, le ...................................</div>
  <div style="margin-top:10px;font-weight:bold;">${opts?.chefScolariteTitre || 'Le Chef de Service de la Scolarité et des Examens'}</div>
  <div style="margin-top:40px;">................................................................</div>
  <div style="margin-top:2px;font-weight:bold;">${opts?.chefScolariteNom || 'Donald Christ MBEMBA LOKO'}</div>
</div>`;

  const pages = pageGroups.map((pg, i) => {
    const isLast = i === pageGroups.length - 1;
    const pageBreak = !isLast ? 'page-break-after:always;' : '';
    // Séparateur visuel entre pages (visible en aperçu, masqué à l'impression)
    const separator = !isLast
      ? `<div class="page-sep" style="border-top:3px dashed #ccc;margin:20px 0;position:relative;text-align:center;">
           <span style="background:#fff;padding:0 10px;position:relative;top:-10px;font-size:10px;color:#999;">— Fin de page ${i + 1} — Salle suivante —</span>
         </div>`
      : '';
    return `<div style="${pageBreak}">
      ${pg.html}
      ${signatureBlock}
      <div style="margin-top:6px;font-size:7px;display:flex;justify-content:space-between;color:#666;">
        <span>Institut International 2i</span>
        <span>Listes de présence par Salle</span>
        <span>${i + 1}/${pageGroups.length}</span>
      </div>
    </div>${separator}`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Listes de Présence</title>
<style>
  @page { size: A4 portrait; margin: 8mm 10mm; }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; }
    .page-sep { display: none !important; }
  }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  body { font-family: ${ff}; font-size: ${11 * scale}px; color: #000; margin: 0; padding: 8px 12px; }
  table { border-collapse: collapse; }
</style>
</head>
<body>
${pages}
</body>
</html>`;
}
